import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Radio } from 'lucide-react';
import type { RevenueAccountsResponse } from '../types';
import { useAccountInvoices } from '../hooks/useRevenue';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const SENSOR_STATUS_LABELS: Record<number, string> = {
  0: 'Unmanaged',
  1: 'Active',
  2: 'Too Low',
  3: 'Too High',
  4: 'To Acknowledge',
  5: 'Inactive',
  6: 'Maintenance',
  8: 'Inhibited',
  9: 'Archived',
};

const COL_COUNT = 6;

interface RevenueAccountsTableProps {
  data?: RevenueAccountsResponse;
  loading: boolean;
  sortKey: string;
  sortDirection: 1 | -1;
  onSort: (key: string) => void;
  onPageChange: (page: number) => void;
}

function SortIcon({ column, sortKey, sortDirection }: { column: string; sortKey: string; sortDirection: 1 | -1 }) {
  if (column !== sortKey) return <ArrowUpDown className="ml-1 h-3 w-3" />;
  return sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />;
}

function AccountInvoicesDetail({ invAccountId }: { invAccountId: string }) {
  const { data, isLoading } = useAccountInvoices(invAccountId);

  if (isLoading) {
    return (
      <TableRow className="bg-muted/20">
        <TableCell />
        <TableCell colSpan={COL_COUNT - 1} className="py-3">
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-4 w-full max-w-md animate-pulse rounded bg-muted" />
            ))}
          </div>
        </TableCell>
      </TableRow>
    );
  }

  const invoices = data?.invoices ?? [];

  if (invoices.length === 0) {
    return (
      <TableRow className="bg-muted/20">
        <TableCell />
        <TableCell colSpan={COL_COUNT - 1} className="py-3 text-sm text-muted-foreground">
          No invoices found for this account.
        </TableCell>
      </TableRow>
    );
  }

  const totalSensors = invoices.reduce((sum, inv) => sum + inv.sensors.length, 0);

  return (
    <>
      {invoices.map((invoice) => (
        <>
          <TableRow key={`inv-${invoice.id}`} className="bg-muted/20 border-0">
            <TableCell />
            <TableCell className="py-1.5 text-sm font-mono text-muted-foreground">
              #{invoice.invoice_number}
            </TableCell>
            <TableCell className="py-1.5 text-sm text-muted-foreground">
              {formatDate(invoice.invoice_date)}
            </TableCell>
            <TableCell className="py-1.5 text-sm text-muted-foreground">
              {invoice.month || '-'}
            </TableCell>
            <TableCell className="py-1.5 text-center text-sm">
              {invoice.sensor_count > 0 ? (
                <Badge variant="outline" className="font-mono text-xs">
                  {invoice.sensor_count}
                </Badge>
              ) : (
                <span className="text-muted-foreground">-</span>
              )}
            </TableCell>
            <TableCell className="py-1.5 text-right text-sm">
              {formatCurrency(invoice.total_amount)}
            </TableCell>
          </TableRow>
          {invoice.sensors.length > 0 && (
            <TableRow key={`sensors-${invoice.id}`} className="bg-blue-50/50 border-0">
              <TableCell />
              <TableCell colSpan={COL_COUNT - 1} className="py-1.5 pl-8">
                <div className="flex flex-wrap gap-1.5">
                  {invoice.sensors.map((sensor) => (
                    <div key={sensor.id} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs">
                      <span className="font-medium">{sensor.name}</span>
                      {sensor.status != null && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {SENSOR_STATUS_LABELS[sensor.status] || `Status ${sensor.status}`}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </TableCell>
            </TableRow>
          )}
        </>
      ))}
      <TableRow className="bg-muted/20 border-0">
        <TableCell />
        <TableCell colSpan={COL_COUNT - 1} className="py-2 text-xs text-muted-foreground">
          <Radio className="inline h-3 w-3 mr-1" />
          {totalSensors} sensor{totalSensors !== 1 ? 's' : ''} across {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
        </TableCell>
      </TableRow>
    </>
  );
}

export function RevenueAccountsTable({ data, loading, sortKey, sortDirection, onSort, onPageChange }: RevenueAccountsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const pageNumber = data?.page_number ?? 0;
  const pageLength = data?.page_length ?? 25;
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageLength);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Accounts</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="cursor-pointer select-none" onClick={() => onSort('account_code')}>
                <span className="flex items-center">Account Code <SortIcon column="account_code" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => onSort('account_name')}>
                <span className="flex items-center">Account Name <SortIcon column="account_name" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none text-right" onClick={() => onSort('total_revenue')}>
                <span className="flex items-center justify-end">Total Revenue <SortIcon column="total_revenue" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none text-center" onClick={() => onSort('sensor_count')}>
                <span className="flex items-center justify-center">Sensors <SortIcon column="sensor_count" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
              <TableHead className="cursor-pointer select-none text-right" onClick={() => onSort('invoice_count')}>
                <span className="flex items-center justify-end">Invoices <SortIcon column="invoice_count" sortKey={sortKey} sortDirection={sortDirection} /></span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell />
                  <TableCell><div className="h-4 w-16 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell><div className="h-4 w-40 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell className="text-right"><div className="ml-auto h-4 w-20 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell className="text-center"><div className="mx-auto h-4 w-10 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell className="text-right"><div className="ml-auto h-4 w-10 animate-pulse rounded bg-muted" /></TableCell>
                </TableRow>
              ))
            ) : data?.accounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="h-24 text-center text-muted-foreground">
                  No accounts found
                </TableCell>
              </TableRow>
            ) : (
              data?.accounts.map((account) => {
                const isExpanded = expandedRows.has(account.id);

                return (
                  <>
                    <TableRow
                      key={account.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleRow(account.id)}
                    >
                      <TableCell className="w-8 px-2">
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{account.account_code}</TableCell>
                      <TableCell>{account.account_name}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(account.total_revenue)}</TableCell>
                      <TableCell className="text-center">
                        {account.sensor_count > 0 ? (
                          <Badge variant="outline" className="font-mono">
                            {account.sensor_count}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{account.invoice_count}</TableCell>
                    </TableRow>
                    {isExpanded && (
                      <AccountInvoicesDetail invAccountId={account.id} />
                    )}
                  </>
                );
              })
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {pageNumber * pageLength + 1} to {Math.min((pageNumber + 1) * pageLength, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={pageNumber === 0} onClick={() => onPageChange(pageNumber - 1)}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {pageNumber + 1} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={pageNumber >= totalPages - 1} onClick={() => onPageChange(pageNumber + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
